/**
 * V3 multi-market selector for the app header.
 *
 * Shows the currently focused market and lets the operator switch
 * between every enabled market. Uses the `useMarket()` context so the
 * selection is global to the app + persisted in localStorage.
 */
import { Group, Menu, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";

import { useMarket } from "../hooks/useMarket";
import type { Market } from "../api/schemas";

function MarketDot({ color }: { color?: string | null }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: color ?? "var(--mantine-color-gray-5)",
        boxShadow: "0 0 0 2px rgba(0,0,0,0.15)",
      }}
    />
  );
}

function MarketRow({ m, selected }: { m: Market; selected: boolean }) {
  return (
    <Group gap={10} wrap="nowrap">
      <MarketDot color={m.color_hex} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={selected ? 700 : 500} style={{ lineHeight: 1.1 }}>
          {m.short_name}
        </Text>
        <Text size="xs" c="dimmed" style={{ lineHeight: 1.1 }}>
          {m.display_name}
        </Text>
      </div>
      {selected && <IconCheck size={16} stroke={2.4} />}
    </Group>
  );
}

/** `compact` (phone header) drops the "Market" caption and tightens padding. */
export function MarketSwitcher({ compact = false }: { compact?: boolean } = {}) {
  const { marketId, market, markets, enabledMarkets, isLoading, setMarketId } = useMarket();

  if (isLoading && markets.length === 0) {
    return <Skeleton height={36} width={compact ? 100 : 180} radius="md" />;
  }

  // Show enabled markets first; allow switching to disabled ones too
  // (e.g. to inspect historical data) under a separate group.
  const enabledIds = new Set(enabledMarkets.map((m) => m.market_id));
  const disabled = markets.filter((m) => !enabledIds.has(m.market_id));

  return (
    <Menu shadow="md" width={260} position="bottom-end" withinPortal>
      <Menu.Target>
        <UnstyledButton
          aria-label="Switch market"
          style={{
            borderRadius: 10,
            padding: compact ? "0 10px" : "6px 12px",
            height: compact ? 36 : undefined,
            border: "1px solid var(--mantine-color-default-border)",
            background: "var(--mantine-color-default)",
          }}
        >
          <Group gap={compact ? 8 : 10} wrap="nowrap" align="center">
            <MarketDot color={market?.color_hex ?? null} />
            <div>
              {compact ? null : (
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.1 }}>
                  Market
                </Text>
              )}
              <Text size="sm" fw={700} style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {market?.short_name ?? marketId}
              </Text>
            </div>
            <IconChevronDown size={16} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {enabledMarkets.length > 0 && (
          <>
            <Menu.Label>Enabled</Menu.Label>
            {enabledMarkets.map((m) => (
              <Menu.Item
                key={m.market_id}
                onClick={() => setMarketId(m.market_id)}
                disabled={m.market_id === marketId}
              >
                <MarketRow m={m} selected={m.market_id === marketId} />
              </Menu.Item>
            ))}
          </>
        )}
        {disabled.length > 0 && (
          <>
            <Menu.Divider />
            <Menu.Label>Available (disabled)</Menu.Label>
            {disabled.map((m) => (
              <Menu.Item
                key={m.market_id}
                onClick={() => setMarketId(m.market_id)}
                disabled={m.market_id === marketId}
              >
                <MarketRow m={m} selected={m.market_id === marketId} />
              </Menu.Item>
            ))}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
